import AppConstants from '../constants/AppConstants';
import store from '../stores/redux';
import api from '../api';
import handleError from '../helpers/requestErrorHandler';
import PublisherActions from './PublisherActions';
import CurrencyActions from './CurrencyActions';
import SessionActions from './SessionActions';
import config from '../config';
import ReportComponent from '../utils/ReportComponent/ReportComponent';

const reportUtils = ReportComponent.utils;

const fixMalformedJSON = (jsonString) => {
    if (!jsonString || typeof jsonString !== 'string') {
        return jsonString;
    }
    
    const trimmed = jsonString.trim();
    
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        return trimmed;
    }
    
    if (trimmed.includes(':') && !trimmed.startsWith('{')) {
        if (trimmed.match(/^"[^"]+"\s*:\s*"[^"]*"$/)) {
            return `{${trimmed}}`;
        }
        
        if (trimmed.match(/^"[^"]+"\s*:\s*"[^"]*"(?:\s*,\s*"[^"]+"\s*:\s*"[^"]*")*$/)) {
            return `{${trimmed}}`;
        }
    }
    
    return trimmed;
};

const { dispatch, getState } = store;
const UserInfoActions = {
    init() {
        PublisherActions.getRanks();
        PublisherActions.getContactTypes();
        CurrencyActions.getCurrencies();

        SessionActions.saveUserID();
        UserInfoActions.getServerStorage();
    },

    loadInfo(params) {
        return api.getUser(params)
        .then(data => {
            dispatch({
                type  : AppConstants.USER_INFO_LOAD_SUCCESS,
                items : data.response || {}
            });
        })
        .catch(err => {
            handleError(err, () => {
                dispatch({
                    type: AppConstants.USER_INFO_LOAD_FAIL,
                    error: err
                });
            });
        });
    },

    edit(params) {
        return api.userEdit(params)
            .then(data => {
                dispatch({
                    type  : AppConstants.USER_EDIT_SUCCESS,
                    items : data.response || {}
                });

                return data.response;
            })
            .catch(err => {
                handleError(err, () => {
                    dispatch({
                        type: AppConstants.USER_EDIT_FAIL,
                        error: err
                    });
                });

                return err;
            });
    },

    changePassword(params) {
        return api.changePassword(params)
            .then(data => {
                return data.response;
            })
            .catch(err => {
                handleError(err, () => {});
                return err;
            });
    },

    getServerStorage(params = {}) {
        params.client_id = config.clientId;

        dispatch({
            type  : AppConstants.SERVER_STORAGE_START_LOADING
        });

        return api.getServerStorage(params)
            .then(data => {
                let parsedResponse = {};
                
                if (data.response) {
                    try {
                        if (typeof data.response === 'object') {
                            parsedResponse = data.response;
                        } else if (typeof data.response === 'string') {
                            const fixedJSON = fixMalformedJSON(data.response);
                            
                            parsedResponse = JSON.parse(fixedJSON);
                        }
                    } catch (error) {
                        parsedResponse = {};
                        
                        if (typeof data.response === 'string') {
                            const match = data.response.match(/"([^"]+)"\s*:\s*"([^"]*)"/);
                            if (match) {
                                parsedResponse[match[1]] = match[2];
                                
                                dispatch({
                                    type: AppConstants.SERVER_STORAGE_SUCCESS,
                                    items: parsedResponse
                                });
                                return;
                            }
                        }
                        
                        dispatch({
                            type: AppConstants.SERVER_STORAGE_FAIL,
                            error: {
                                code: 500,
                                message: 'Server returned invalid data format. Some features may not work correctly.',
                                details: `JSON parsing error: ${error.message}. Raw response: ${data.response}`
                            }
                        });
                        return;
                    }
                }
                
                dispatch({
                    type  : AppConstants.SERVER_STORAGE_SUCCESS,
                    items : parsedResponse
                });
            })
            .catch(err => {
                handleError(err, () => {});
                return err;
            });
    },

    setServerStorage() {
        let storeData = getState().serverStorage.store;
        
        const cleanData = (obj) => {
            if (obj === null || obj === undefined) return obj;
            if (typeof obj === 'function') return '[Function]';
            if (typeof obj !== 'object') return obj;
            if (obj instanceof Date) return obj.toISOString();
            if (Array.isArray(obj)) {
                return obj.map(item => cleanData(item));
            }
            
            const cleaned = {};
            for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                    try {
                        cleaned[key] = cleanData(obj[key]);
                    } catch (e) {
                        cleaned[key] = '[Error cleaning]';
                    }
                }
            }
            return cleaned;
        };
        
        let cleanedStoreData = cleanData(storeData);
        
        const dataString = JSON.stringify(cleanedStoreData);
        const maxSize = 1024 * 1024;
        
        if (dataString.length > maxSize) {
            const reducedData = {
                dataTable: cleanedStoreData.dataTable,
                report: {
                    metrics: cleanedStoreData.report?.metrics || []
                }
            };
            
            const reducedDataString = JSON.stringify(reducedData);
            
            if (reducedDataString.length > maxSize) {
                return Promise.reject(new Error('Data size too large'));
            }
            
            cleanedStoreData = reducedData;
        }
        
        if (window.location.search.includes('testMinimalData=true')) {
            cleanedStoreData = {
                dataTable: { version: 3 },
                report: { metrics: ['Visits', 'Visitors'] }
            };
        }
        
        if (window.location.search.includes('testFormat1=true')) {
            // Try sending data in the same format as received
            cleanedStoreData = JSON.stringify({
                dataTable: { version: 3 },
                report: { metrics: ['Visits', 'Visitors'] }
            });
        }
        
        if (window.location.search.includes('testFormat2=true')) {
            cleanedStoreData = {
                dataTable: { version: 3 }
            };
        }

        let params = {
            client_id: config.clientId,
            data: typeof cleanedStoreData === 'string' ? cleanedStoreData : JSON.stringify(cleanedStoreData)
        };
        
        if (!cleanedStoreData.dataTable || !cleanedStoreData.report) {
            return Promise.reject(new Error('Invalid data structure'));
        }
        
        if (cleanedStoreData.report.metrics && !Array.isArray(cleanedStoreData.report.metrics)) {
            if (typeof cleanedStoreData.report.metrics === 'string') {
                cleanedStoreData.report.metrics = reportUtils.getKeysMetric(cleanedStoreData.report.metrics);
            }
        }

        dispatch({
            type: AppConstants.SERVER_STORAGE_START_LOADING
        });

        return api.setServerStorage({ ...params })
            .then(() => {
                dispatch({
                    type  : AppConstants.SERVER_STORAGE_SUCCESS,
                    items : {}
                });
                
                return UserInfoActions.getServerStorage();
            })
            .catch(err => {
                handleError(err, () => {});
                return err;
            });
    }
};

if (typeof window !== 'undefined' && window.location.search.includes('testJSONFix=true')) {
    
    const testCases = [
        '"test":"refactored"',
        '{"test":"refactored"}',
        '"key":"value","key2":"value2"',
        '{"key":"value","key2":"value2"}',
        'invalid text',
        '{"incomplete":',
    ];
    
    testCases.forEach((testCase) => {
        try {
            const fixed = fixMalformedJSON(testCase);
            JSON.parse(fixed);
        } catch (error) {
        }
    });
}

export default UserInfoActions;
